import { Injectable, inject , signal } from '@angular/core';
import { Observable, Observer, BehaviorSubject, forkJoin, of, throwError, timer } from 'rxjs';
import { WaypointService } from '../core/waypoint.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { WebviewService } from '../platform/webview.service';
import { SettingsService } from './settings.service';
import device from 'current-device';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { retry, switchMap } from 'rxjs/operators';
import { LanguageService } from './language.service';
import { HistoryElement } from '../../models/history';
import { ECar, ECarSettings } from '../../models/ecar';
import { ECarType } from '../../models/ecar-type';
import { RoutingSettings } from '../../models/routing-settings';
import { User } from '../../models/user';
import { UserProfile } from '../../models/user-profile';
import { TripPoint } from '../../models/trip';
import { SubscriptionPlatform, UserSubscription } from '../../models/user-subscription';
import { HttpService } from '../core/http.service';
import { ScriptLoaderService } from '../platform/script-loader.service';

/**
 * Social sign-in SDKs. They are not in index.html: Google and Apple load when a step with the sign-in
 * buttons opens (prepareSocialSignIn), Facebook only when its button is used (docs/cookie-consent.md).
 */
const GOOGLE_SIGN_IN_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_SIGN_IN_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
// The SDK initialises itself from the hash parameters.
const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js#version=v2.7&appId=789073781835841&xfbml=true&cookie=true&status=true';

// The backend refuses to add vehicles beyond this many per user.
export const MAX_USER_VEHICLES = 5;

export const enum SubscriptionType {
  Free = "free",
  ProMonthly = "evnavigation.pro.monthly",
  ProYearly = "evnavigation.pro.yearly",
  ProplusMonthly = "evnavigation.proplus.monthly",
  ProplusYearly = "evnavigation.proplus.yearly",
  ProplusVoucher = "evnavigation.proplus.voucher"
}

@Injectable({
  providedIn: 'root'
})

export class AccountService {

  private readonly _isAuthorized = signal<boolean>(false);
  public ObservableIsAuthorized = new BehaviorSubject<boolean | null>(null);
  public ObservableUser = new BehaviorSubject<User | null>(null);
  public ObservableUserVehicles = new BehaviorSubject<ECar[] | null>(null);
  private selectedUserCar: ECar | null = null;
  public ObservableSelectedUserCar = new BehaviorSubject<ECar | null>(null);
  public ObservableOpenCarSelectorDialog = new BehaviorSubject<boolean | null>(null);
  public ObservableVehicleLoader = new BehaviorSubject<boolean | null>(null);
  public ObservableOpenEditProfileDialog = new BehaviorSubject<boolean | null>(null);
  public ObservableSocialLoginResponse = new BehaviorSubject<{ AuthResponse: any, ErrorMessage: string | null } | null>(null);
  public ObservableSubscriptionType!: BehaviorSubject<SubscriptionType>;
  public ObservableSubscriptionFailed = new BehaviorSubject<boolean>(false);
  public ObservableSubscriptionLoader = new BehaviorSubject<boolean>(false);
  private historyLastSelectedVehicle: ECar | null = null;
  private userSubscription: UserSubscription = {
    AutoRenew: false, ExpireDate: null!, LastPaymentDate: null!, NextPaymentDate: null!, SubscriptionType: SubscriptionType.Free,
    Platform: SubscriptionPlatform.Web, DowngradeAtPeriodEnd: false
  };
  public ObservableSubscriptionChangedDialog = new BehaviorSubject<SubscriptionType | null>(null);
  public ObservableVoucherInfoDialog = new BehaviorSubject<any>(null);

  public accountInit: boolean = false;
  public user!: User;
  private ECars!: ECarType[];
  private readonly _social = signal<boolean>(false);
  /** Signal-backed: read from templates to decide whether to offer a password change. */
  public get social(): boolean { return this._social(); }
  public set social(value: boolean) { this._social.set(value); }
  private provider: string = "";

  private readonly waypointService = inject(WaypointService);
  private readonly cookieService = inject(CookieService);
  public readonly webviewService = inject(WebviewService);
  private readonly settingsService = inject(SettingsService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  private readonly languageService = inject(LanguageService);
  private readonly httpService = inject(HttpService);
  private readonly scriptLoader = inject(ScriptLoaderService);

  constructor() {
    this.user = new User();
    this.user.Profile = new UserProfile();
    this.ObservableSubscriptionType = new BehaviorSubject<SubscriptionType>(this.userSubscription.SubscriptionType);

    this.webviewService.AddAppleSignInCallback((isSuccess: boolean, userId: string, accessToken: string) => {
      this.handleAppleSignInResponse(isSuccess, userId, accessToken);
    });
    this.webviewService.addSubscriptionSuccessCallback((isSuccess: boolean, subscriptionType: SubscriptionType) => {
      if (isSuccess) {
        this.handleSubscriptionSuccessCallback(subscriptionType);
        this.ObservableSubscriptionLoader.next(true);
      }
    });
    this.webviewService.addSubscriptionCancelCallback(() => {
      this.handleSubscriptionCancelCallback();
    });

  }

  /** Retry cadence while waiting for the async GIS script / its rendered button. */
  private static readonly GIS_POLL_MS = 100;
  private static readonly GIS_POLL_MAX = 50;

  /** Off-screen host of the official GIS button; see initGoogleLogin(). */
  private gisButtonHost: HTMLElement | null = null;

  /** Loads the Google and Apple sign-in SDKs; called by the account dialog steps that show their buttons. */
  public prepareSocialSignIn(): void {
    if (this.webviewService.IsGPSTWebview()) return;
    this.scriptLoader.load(GOOGLE_SIGN_IN_SRC).then(() => this.initGoogleLogin()).catch(() => { });
    this.scriptLoader.load(APPLE_SIGN_IN_SRC).catch(() => { });
  }

  private initGoogleLogin(attempt: number = 0): void {
    if (this.gisButtonHost) return;
    const gis = (globalThis as any)['google']?.accounts?.id;
    if (!gis) {
      // The gsi/client script is loaded async and may land after bootstrap.
      if (attempt < AccountService.GIS_POLL_MAX) {
        setTimeout(() => this.initGoogleLogin(attempt + 1), AccountService.GIS_POLL_MS);
      }
      return;
    }

    gis.initialize({
      client_id: '27060447138-oun6fqos4lgeas6okc07f0d10f9vns69.apps.googleusercontent.com',
      cookiepolicy: 'single_host_origin',
      scope: 'profile email',
      // Keep the button on the classic popup flow. FedCM (One Tap, or the button's
      // FedCM mode) is embargoed by Chrome for the site once the user dismisses it.
      use_fedcm_for_button: false,
      callback: (resp) => {
        this.handleGoogleSignInResponse(true, resp.clientId, resp.credential);
      }
    });

    // Sign In With Google on the web goes through Google's rendered button: its click
    // opens the account-chooser popup, which sits above any modal and does not depend
    // on FedCM. The button markup arrives asynchronously from Google, so it is rendered
    // once here, invisibly, and the custom "G" buttons in the account dialog forward
    // their clicks to it (signInWithGoogle). accounts.id.prompt() (One Tap) is not used:
    // Firefox draws it as an in-page iframe under the modal backdrop, and on Chromium
    // >= 142 the GIS client forces it through FedCM, which Chrome disables for the site
    // after the user dismisses it once.
    const host = document.createElement('div');
    host.dataset['testid'] = 'google-sign-in-hidden-btn';
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;overflow:hidden;visibility:hidden;';
    document.body.appendChild(host);
    gis.renderButton(host, { type: 'icon', shape: 'circle', size: 'large', theme: 'outline' });
    this.gisButtonHost = host;
  }

  // AUTHORIZATION

  /** Reading this from a template registers the view as a consumer of the auth signal. */
  public getIsAuthorized(): boolean {
    return this._isAuthorized();
  }

  public setIsAuthorized(isAuthorized: boolean): void {
    this._isAuthorized.set(isAuthorized);
    this.settingsService.setIsAuthorized(isAuthorized);
    this.ObservableIsAuthorized.next(isAuthorized);
  }

  public setToken(token: string, expires: number, socialProvider?: string) {
    this.httpService.setToken(token);
    this.setIsAuthorized(true);

    if (expires) {
      this.cookieService.set("evt", socialProvider ? token + "social" : token, expires, "/");
      this.webviewService.SetAccessToken(token, expires);
    }

    if (socialProvider) {
      this.social = true;
      this.provider = socialProvider;
      this.cookieService.set("accountProvider", socialProvider);
    }
  }

  // REGISTRATION

  // checking email address availability
  public isExist(emailAddress: string): Observable<any> {
    return this.httpService.checkUserExist(emailAddress);
  }

  // checking the country of your location
  public getCountry(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.getUserCountry().subscribe({
        next: (resp: any) => {
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // registering a new user
  public registerUser(profile: UserProfile, password: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.registerUser(profile, password, this.languageService.getLanguage())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            if (error.error?.error?.avatar) {
              observer.error({ failed: { error: error.error.error.avatar } });
            }
            else {
              observer.error({ failed: { error: this.languageService.languageJSON.Global_SomethingWentWrong } });
            }
            observer.complete();
          }
        });
    });
  }

  // email verification for activation profile
  public verifyEmail(urlParam: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.verifyEmail(urlParam)
        .subscribe({
          next: (resp: any) => {
            this.setToken(resp.success.token, 1);
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // send a new email with activation url
  public resendActivationEmail(emailAddress: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.resendEmail(emailAddress)
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error({ failed: { error: this.languageService.languageJSON.Global_SomethingWentWrong } });
            observer.complete();
          }
        });
    });
  }

  // SOCIAL REGISTRATION/LOGIN

  // login with social media account
  public authenticateSocial(provider: string, providerId: string, accessToken: string, native?: boolean): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.authenticateSocial(provider, providerId, accessToken, this.webviewService.IsGPSTWebview(), native)
        .subscribe({
          next: (resp: any) => {
            if (resp.success) {
              this.initSocialProfile(resp, provider).subscribe({
                next: (respInit) => {
                  observer.next(resp);
                  observer.complete();
                },
                error: (error) => {
                  observer.error(error);
                  observer.complete();
                }
              });
            }
            else {
              // Rejected by the backend: hand the response (with its `failed` message) to the caller.
              observer.next(resp);
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // set authorization, store token in cookies
  public initSocialProfile(resp: any, provider: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.setToken(resp.success.token, 14, provider);

      this.loadUserProfile().subscribe({
        next: () => {
          if (resp.success.error || (!this.user.Profile.FirstName || !this.user.Profile.LastName)) {
            this.ObservableOpenEditProfileDialog.next(true);
          }
          // wait user profile/settings/subscriptions/vehicles http calls
          this.initUserData(false, true, true).subscribe({
            next: () => {
              observer.next(resp);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
              observer.complete();
            }
          });
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // LOGIN, INITIALIZATION

  // user login
  public login(login: { email: string, password: string, rememberMe: boolean }): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.loginUser(login.email, login.password)
        .subscribe({
          next: (resp: any) => {
            if (resp.success) {
              this.setToken(resp.success.token, login.rememberMe ? 365 : 1);
              this.initUserData(true, true, true).subscribe({
                next: () => {
                  observer.next(resp);
                  observer.complete();
                },
                error: (error) => {
                  observer.error(error);
                  observer.complete();
                }
              });
            }
            else if (resp.failed && resp.failed == "USER_ERR_205") {
              observer.next(resp);
              observer.complete();
            }
            else {
              // Any other rejection: the caller shows its sign-in error.
              observer.error(resp);
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public initAccount(): boolean {
    let evtoken = this.cookieService.get("evt");
    if (evtoken && evtoken != "null") {
      if (evtoken.slice(-6) == "social") {
        this.setToken(evtoken.slice(0, -6), 0);
        this.social = true;
      }
      else {
        this.setToken(evtoken, 0);
      }
      this.setIsAuthorized(true);
    }

    const accountProvider = this.cookieService.get("accountProvider");
    if (accountProvider && accountProvider != "null") {
      this.provider = accountProvider;
    }

    return this._isAuthorized();
  }

  public initUserData(loadUserProfile: boolean, getUserSettings: boolean, getUserVehicles: boolean): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      const accountHttpCalls: Observable<any>[] = [];

      if (loadUserProfile) {
        accountHttpCalls.push(this.loadUserProfile());
      }
      if (getUserSettings) {
        accountHttpCalls.push(this.getUserSettings());
      }
      if (getUserVehicles) {
        accountHttpCalls.push(this.getUserVehicles());
      }

      // wait user profile/settings/subscriptions/vehicles http calls
      forkJoin(accountHttpCalls).subscribe({
        next: (resp) => {
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // get user profile data
  public loadUserProfile(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.getUser()
        .subscribe({
          next: (resp: any) => {
            if (resp.email) {
              this.setUserData(resp);
              this.parseUserSubscription(resp);
            }
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setUserData(resp: any): void {
    this.setUserProfileData(resp);
    if (resp.addresses != null) {
      this.settingsService.setAddresses(this.settingsService.addressesJSONtoAddresses(JSON.parse(resp.addresses)));
    }

    this.applyChargingStationLists(resp.stations);
    this.applyChargingOperatorLists(resp.operators);

    if (resp.disabledChargingStation != null) {
      this.settingsService.setDisabledChargingStationIds(JSON.parse(resp.disabledChargingStation));
    }

    if (resp.stripe_id){
      this.user.TrialUsed = true;
    }

    this.ObservableUser.next(this.user);
  }

  private setUserProfileData(resp: any): void {
    this.user.Profile.Email = resp.email;
    this.user.Profile.FirstName = resp.first_name;
    this.user.Profile.LastName = resp.last_name;
    this.user.Profile.Country = resp.country;
    this.user.Profile.Phone = resp.phone_number;
    this.user.Profile.Newsletter = resp.newsletter;
    this.user.Profile.Avatar = resp.avatar;
    this.user.Profile.Id = resp.id;
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.SetUserId(resp.id);
    }
    if (this.user.Profile.Avatar == "users/default.png" || this.user.Profile.Avatar == "users/avatar/default.png") {
      this.user.Profile.Avatar = undefined;
    }
  }

  /** `stationsJSON` is the server's `{ disabled?, favorite? }` JSON; a list it leaves out is cleared. */
  private applyChargingStationLists(stationsJSON: string | undefined): void {
    const stations = stationsJSON ? JSON.parse(stationsJSON) : null;
    this.settingsService.setDisabledChargingStationIds(stations?.disabled ? stations.disabled : []);
    this.settingsService.setFavoriteChargingStationIds(stations?.favorite ? stations.favorite : []);
  }

  /** `operatorsJSON` is the server's `{ disabled?, favorite? }` JSON; a list it leaves out is cleared. */
  private applyChargingOperatorLists(operatorsJSON: string | undefined): void {
    const operators = operatorsJSON ? JSON.parse(operatorsJSON) : null;
    this.settingsService.setDisabledChargingOperatorIds(operators?.disabled ? operators.disabled.map(Number) : []);
    this.settingsService.setFavoriteChargingOperatorIds(operators?.favorite ? operators.favorite.map(Number) : []);
  }

  public parseUserSubscription(resp: any): void {
    if (resp.subscriptions?.subscription_type || resp.subscriptions?.type == "voucher") {
      let subscriptionType = "evnavigation." + resp.subscriptions?.subscription_type + "." + resp.subscriptions?.renew_type;

      const userSubscription: UserSubscription = {
        AutoRenew: resp.subscriptions.autorenew == "true",
        ExpireDate: new Date(resp.subscriptions.expire_date),
        LastPaymentDate: new Date(resp.subscriptions.last_payment_date),
        NextPaymentDate: resp.subscriptions.next_payment_date ? new Date(resp.subscriptions.next_payment_date) : null,
        SubscriptionType: subscriptionType as SubscriptionType,
        Platform: resp.subscriptions.type,
        DowngradeAtPeriodEnd: resp.subscriptions.downgrade_at_period_end == 1
      }

      if (resp.subscriptions?.type == "voucher") {
        userSubscription.SubscriptionType = SubscriptionType.ProplusVoucher;
        userSubscription.AutoRenew = false;
      }

      this.setUserSubscription(userSubscription);
    }
    else {
      this.setUserSubscription({
        AutoRenew: false, ExpireDate: null!, LastPaymentDate: null!, NextPaymentDate: null!, SubscriptionType: SubscriptionType.Free,
        Platform: SubscriptionPlatform.Web, DowngradeAtPeriodEnd: false
      });
    }

    if (!this.getUserSubscriptionType().includes(".proplus") && localStorage.getItem("voucherToken")) {
      this.addVoucherSubscription(localStorage.getItem("voucherToken")!).subscribe();
    }
  }

  public initAllVehicles(allVehicles: ECarType[]): void {
    this.ECars = allVehicles;
    if (!this.accountInit) {
      this.initAccount();
      this.accountInit = true;
    }
  }

  // getting user vehicle list
  public getUserVehicles(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.ObservableVehicleLoader.next(true);
      this.httpService.getUserVehicles().subscribe({
        next: (resp: any) => {
          this.ObservableVehicleLoader.next(false);
          if (resp.success && resp.success.length > 0) {
            this.initUserVehicles(resp.success);
          }
          else {
            // Empty vehicle list -> signal the app to (re)show the mandatory first-vehicle
            // dialog. RouterFlowHandler subscribes to this and keeps it open until a
            // vehicle is saved. Fires on every empty result (startup, post email
            // verification, after deleting the last car).
            this.ObservableOpenCarSelectorDialog.next(true);
          }
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          this.checkErrorStatus(error);
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // processing user vehicles data
  private initUserVehicles(resp: any): void {
    if (!resp) {
      return;
    }
    this.user.Ecars = [];
    for (const carData of resp) {
      const userEcar = this.createUserEcar(carData);

      this.user.Ecars.push(userEcar);

      if (userEcar.selected === true && userEcar.ECarType?.Icon) {
        this.selectedUserCar = userEcar;
        this.ObservableSelectedUserCar.next(userEcar);
      }
    }
    if (this.hasVehicleTypes()) {
      this.ObservableUserVehicles.next(this.user.Ecars);
    }
  }

  /** True once the vehicle type catalogue (initAllVehicles) is loaded and not empty. */
  private hasVehicleTypes(): boolean {
    return this.ECars != null && this.ECars != undefined && this.ECars.length > 0;
  }

  private createUserEcar(carData: any): ECar {
    const userEcar = new ECar();
    userEcar.userVehicleID = carData.id;
    userEcar.name = (carData.name ? carData.name : "");
    userEcar.ECarType = null;
    userEcar.selected = ((carData.selected != undefined) && (Number.parseInt(carData.selected) === 1));
    if (carData.settings != undefined) {
      userEcar.settings = JSON.parse(carData.settings);
    }
    userEcar.ECarType = this.findVehicleTypeCopy(carData.vehicle_id);
    if (userEcar.ECarType) {
      userEcar.ECarType.ChargerTypes = JSON.parse(carData.charger_types);
    }
    return userEcar;
  }

  /** A shallow copy of the catalogue entry, so per-car charger types don't leak into the catalogue. */
  private findVehicleTypeCopy(vehicleId: any): ECarType | null {
    if (!this.hasVehicleTypes()) {
      return null;
    }
    const eCar = this.ECars.find((type) => type.Id == vehicleId);
    return eCar ? { ...eCar } : null;
  }

  // GETTING, SETTING USER DATA

  public getUserProfile(): User {
    return this.user;
  }

  // changing user profile
  public setUser(profile: UserProfile): Observable<any> {
    return new Observable((observer: Observer<any>) => {

      this.httpService.updateUserProfile(profile)
        .subscribe({
          next: (resp: any) => {
            // setting changed user profile values
            if (resp.success) {
              resp.success.newsletter = resp.success.newsletter == "1";
              this.setUserData(resp.success);
            }
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            if (error.status == 401 && error.error?.error?.avatar) {
              //error msg
            }
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public getUserAvatar(): string {
    return this.user.Profile.Avatar as string;
  }

  public setUserSubscriptionType(subscriptionType: SubscriptionType) {
    this.userSubscription.SubscriptionType = subscriptionType;
    this.ObservableSubscriptionType.next(subscriptionType);
  }

  public getUserSubscriptionType(): SubscriptionType {
    return this.userSubscription.SubscriptionType;
  }

  public setUserSubscription(userSubscription: UserSubscription) {
    this.userSubscription = userSubscription;
    this.ObservableSubscriptionType.next(userSubscription.SubscriptionType);
  }

  public getUserSubscription(): UserSubscription {
    return this.userSubscription;
  }

  public cancelUserSubscription() {
    return new Observable((observer: Observer<any>) => {
      this.httpService.cancelUserSubscription()
        .subscribe({
          next: (resp: any) => {
            if (resp.message == "Subscription cancelled") {
              observer.next(resp);
              observer.complete();
            }
            else {
              observer.error("error");
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // adding a new vehicle to user account
  public addVehicle(vehicleId: number, vehicleName: string, chargerTypes: number[], batteryStateOfHealth: number, fastChargeLimit: number, sliderSettings?: Partial<ECarSettings>): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      const settings = {
        "batteryLevel": sliderSettings?.batteryLevel ?? 73,
        "weight": sliderSettings?.weight ?? 2,
        "tirePressure": sliderSettings?.tirePressure ?? 2.3,
        "userBehavior": sliderSettings?.userBehavior ?? 50,
        "speedLimiter": sliderSettings?.speedLimiter ?? 100,
        "batterySafetyLimitRha": sliderSettings?.batterySafetyLimitRha ?? 50,
        "batterySafetyLimitRoute": sliderSettings?.batterySafetyLimitRoute ?? 15,
        "minBatteryAtDestination": sliderSettings?.minBatteryAtDestination ?? 10,
        "batteryStateOfHealth": batteryStateOfHealth, "fastChargeLimit": fastChargeLimit,
        "tripRecording": sliderSettings?.tripRecording ?? 1
      }
      this.httpService.addUserVehicle(vehicleId, vehicleName, chargerTypes, settings)
        .subscribe({
          next: (resp: any) => {
            if (resp.success && this.user.getSelectedCar() == undefined) {
              this.selectVehicle(resp.success.id).subscribe();
            }
            this.getUserVehicles().subscribe(() => {
              observer.next(resp);
              observer.complete();
            });
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // change active vehicle
  public selectVehicle(vehicleId: number): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.selectUserVehicle(vehicleId)
        .subscribe({
          next: (resp: any) => {
            this.getUserVehicles().subscribe(() => {
              observer.next(resp);
              observer.complete();
            });
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // change user vehicle details
  public editVehicle(userVehicleId: number, vehicleId: number | null, vehicleName: string | null, chargerTypes: number[] | null, settings: string, reloadVehicles: boolean): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.modifyUserVehicle(userVehicleId, vehicleId, vehicleName, chargerTypes, settings)
        .subscribe({
          next: (resp: any) => {
            if (reloadVehicles) {
              this.getUserVehicles().subscribe();
            }
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // remove a vehicle from the user account
  public removeVehicle(vehicleId): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.removeUserVehicle(vehicleId)
        .subscribe({
          next: (resp: any) => {
            this.getUserVehicles().subscribe();
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // modify user password
  public changePassword(emailAddress: string, currentPassword: string, newPassword: string, cNewPassword: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {

      this.httpService.changeUserPassword(emailAddress, currentPassword, newPassword, cNewPassword)
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // request resetting password
  public getForgottenPassword(emailAddress: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.requestPasswordReset(emailAddress)
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // load user settings
  public getUserSettings(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.getUserSettings()
        .subscribe({
          next: (resp: any) => {
            if (resp.success) {
              this.parseUserSettingsJSONFields(resp.success);
              this.applyUserSettings(resp.success);
            }

            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  /** Settings fields the server sends JSON-encoded, in the order they are parsed. */
  private static readonly JSON_ENCODED_USER_SETTINGS = ['mapsettings', 'routing', 'unit', 'voiceguidance', 'voiceInputTutorialDone', 'tracking'];

  /** Parses the JSON-encoded fields in place (the caller emits the same object); falsy values are left as they are. */
  private parseUserSettingsJSONFields(userSettings: any): void {
    for (const field of AccountService.JSON_ENCODED_USER_SETTINGS) {
      if (userSettings[field]) {
        userSettings[field] = JSON.parse(userSettings[field]);
      }
    }
  }

  /** Applies every setting the server sent (null / undefined fields are skipped). */
  private applyUserSettings(userSettings: any): void {
    if (userSettings.routing != null) {
      this.applyRoutingIfChanged(this.settingsService.routingJSONtoRoutingSettings(userSettings.routing));
    }

    if (userSettings.voiceguidance != null) {
      this.settingsService.setVoiceGuidence(this.settingsService.voiceGuidenceJSONtoVoiceGuidenceSettings(userSettings.voiceguidance));
    }

    if (userSettings.voiceInputTutorialDone != null) {
      this.settingsService.setVoiceInputTutorialDone(userSettings.voiceInputTutorialDone);
    }

    if (userSettings.unit != null) {
      this.settingsService.setUnit(this.settingsService.unitJSONtoUnitSettings(userSettings.unit));
    }

    // Map settings without a skin are ignored.
    if (userSettings.mapsettings?.skin != null) {
      this.settingsService.setMap(this.settingsService.mapJSONtoMapSettings(userSettings.mapsettings));
    }

    if (userSettings.tracking != null) {
      this.settingsService.setTracking(userSettings.tracking);
    }

    if (userSettings.language != null) {
      this.languageService.setLanguage(userSettings.language);
    }
  }

  private applyRoutingIfChanged(routingSettings: RoutingSettings): void {
    const current = this.settingsService.getRouting();
    if (!(current.Ferries == routingSettings.Ferries
      && current.Highways == routingSettings.Highways
      && current.TollRoads == routingSettings.TollRoads)) {
      this.settingsService.setRounting(routingSettings);
    }
  }

  setUserSettings(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      const unit = this.settingsService.getUnit();
      const routing = this.settingsService.getRouting();
      const voiceguidance = this.settingsService.getVoiceGuidence();
      const mapsettings = this.settingsService.getMap();
      const voiceInputTutorialDone = this.settingsService.getVoiceInputTutorialDone();
      const tracking = this.settingsService.getTracking();
      const language = this.languageService.getLanguage();

      this.httpService.updateUserSettings(unit, routing, voiceguidance, mapsettings, voiceInputTutorialDone, tracking, language)
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // set home/work address
  public setUserAddresses(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.updateUserAddress(this.settingsService.getAddresses())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setDisabledChargingStations(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.updateDisabledChargingStations(this.settingsService.getDisabledChargingStationIds())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setFavoriteChargingStations(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.updateFavoriteChargingStations(this.settingsService.getFavoriteChargingStationIds())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setDisabledChargingOperators(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.updateDisabledChargingOperators(this.settingsService.getDisabledChargingOperatorIds())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setFavoriteChargingOperators(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      const formData = new FormData();
      formData.append("ids", JSON.stringify(this.settingsService.getFavoriteChargingOperatorIds()));
      this.httpService.updateFavoriteChargingOperators(this.settingsService.getFavoriteChargingOperatorIds())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          }, error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public voucherCheck(voucherId: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.voucherCheck(voucherId)
        .subscribe({
          next: (resp: any) => {
            if (!this.getIsAuthorized()) {
              this.ObservableVoucherInfoDialog.next(resp);
            }
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.ObservableVoucherInfoDialog.next(error.error);
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public addVoucherSubscription(voucherId: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      localStorage.removeItem("voucherToken");
      if (this.getUserSubscriptionType().includes(".proplus")) {
        // Only reachable through a voucher link; the subscriptions dialog hides vouchers from proplus users.
        this.ObservableVoucherInfoDialog.next({ error: "Voucher_AlreadyProPlus" });
        observer.complete();
      }
      else {
        this.httpService.addVoucherSubscription(voucherId)
          .subscribe({
            next: (resp: any) => {
              if (resp.success) {
                this.ObservableSubscriptionChangedDialog.next(SubscriptionType.ProplusMonthly);
                this.loadUserProfile().subscribe();
                observer.next(resp);
                observer.complete();
              }
              else {
                observer.next(resp);
                observer.complete();
              }
            },
            error: (error) => {
              this.ObservableVoucherInfoDialog.next(error.error);
              this.checkErrorStatus(error);
              observer.error(error);
              observer.complete();
            }
          });
      }
    });
  }

  /**
   * when the mobile app callback the frontend with success, we should check the backend that the subscription already activated
   */
  private handleSubscriptionSuccessCallback(subscriptionType: SubscriptionType) {
    this.fetchWithRetryForSubscription(2, 5000, subscriptionType, "true").subscribe({
      next: (resp) => {
        this.setUserData(resp);
        this.parseUserSubscription(resp);
        this.ObservableSubscriptionChangedDialog.next(subscriptionType);
      },
      error: (resp) => {
        this.ObservableSubscriptionType.next(this.ObservableSubscriptionType.value);
        this.ObservableSubscriptionFailed.next(true);
      }
    });
  }

  fetchWithRetryForSubscription(maxRetries: number, delayMs: number, subscriptionType: SubscriptionType, autorenew: string) {
    return this.httpService.getUser().pipe(
      switchMap((resp) => {

        if (resp?.subscriptions?.subscription_type &&
          ("evnavigation." + resp.subscriptions?.subscription_type + "." + resp.subscriptions?.renew_type == subscriptionType ||
            (this.webviewService.IsGPSTWebview() && device.ios()) && resp.subscriptions?.downgrade_at_period_end == 1)
          && resp.subscriptions?.autorenew == autorenew) {
          // Stop retrying if the desired object is found
          return of(resp);
        } else {
          // Trigger retry when the desired object is not found
          return throwError(() => new Error('Object not found'));
        }
      }),
      retry({
        count: maxRetries,
        delay: () => timer(delayMs)
      })
    );
  }

  private handleSubscriptionCancelCallback() {
    this.setUserSubscription(
      {
        AutoRenew: false, ExpireDate: null!, LastPaymentDate: null!, NextPaymentDate: null!,
        SubscriptionType: SubscriptionType.Free, Platform: SubscriptionPlatform.Web,
        DowngradeAtPeriodEnd: false
      });
  }

  public deleteUser(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.deleteUser()
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // LOGOUT

  // log out 401 error status
  private checkErrorStatus(error): void {
    if (error.status && error.status == 401) {
      this.logout();
    }
  }

  public logout(): void {
    switch (this.provider) {
      case "google":
        this.signOutGoogle();
        break;
      case "facebook":
        this.signOutFacebook();
        break;
    }
    this.provider = "";
    this.settingsService.initSettings();
    this.setIsAuthorized(false);
    this.user = new User();
    this.user.Profile = new UserProfile();
    this.httpService.setToken(null!);
    this.social = false;
    this.setUserSubscription({ AutoRenew: false, ExpireDate: null!, LastPaymentDate: null!, NextPaymentDate: null!, SubscriptionType: SubscriptionType.Free, Platform: SubscriptionPlatform.Web, DowngradeAtPeriodEnd: false });
    this.cookieService.delete("evt");
    // Otherwise initAccount restores the old provider after a reload, even for a later password sign-in.
    this.cookieService.delete("accountProvider");
    this.webviewService.ClearAccessToken();
  }

  public getHistory(carId: number): Observable<HistoryElement[]> {
    return new Observable((observer: Observer<HistoryElement[]>) => {
      // getting new history
      if (this.user.HistoryList[carId.toString()] == undefined) {
        this.httpService.getEvnaviActivities(carId.toString()).subscribe({
          next: (resp: any) => {
            resp = resp.map(el => {
              return {
                id: el.id,
                startLocation: el.from_loc,
                targetLocation: el.end_loc,
                startTime: el.from ? el.from.replace(/\s/, 'T') : el.from,
                distance: Math.round(el.distance * 10) / 10,
                totalTime: JSON.parse(el.time_duration),
                avgConsumption: (Math.round(el.averageConsumption * 10) / 10),
                totalConsumption: (Math.round(el.consumption * 10) / 10)
              }
            });

            this.user.HistoryList[carId.toString()] = resp;
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
      }
      // reload previously loaded history
      else {
        observer.next(this.user.HistoryList[carId.toString()]);
        observer.complete();
      }
    });
  }

  public getHistoryLastSelectedVehicle(): ECar | null {
    return this.historyLastSelectedVehicle;
  }

  public setHistoryLastSelectedVehicle(historyLastSelectedVehicle: ECar | null): void {
    this.historyLastSelectedVehicle = historyLastSelectedVehicle;
  }

  public getTrack(logId: number): Observable<TripPoint[]> {
    return new Observable((observer: Observer<TripPoint[]>) => {
      // getting new track
      if (this.user.TipList[logId.toString()] == undefined) {
        this.httpService.readEvnaviFiles(logId.toString()).subscribe({
          next: (resp: any) => {
            this.user.TipList[logId] = resp;
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
      }
      // reload previously loaded track
      else {
        observer.next(this.user.TipList[logId.toString()]);
        observer.complete();
      }
    });
  }

  public uploadLog(userVehicleId, data): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.logEvnaviData(userVehicleId, data).subscribe({
        next: (resp: any) => {
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  public deleteLogById(carId: number, logId: number): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.deleteEvnaviLogById(logId.toString()).subscribe({
        next: (resp: any) => {
          this.user.HistoryList[carId.toString()] = undefined;
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  public deleteLogByVehicle(carId: number): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.deleteEvnaviLogByVehicle(carId.toString()).subscribe({
        next: (resp: any) => {
          this.user.HistoryList[carId.toString()] = undefined;
          observer.next(resp);
          observer.complete();
        }, error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // SOCIAL SIGN IN

  private handleFacebookSignInResponse(isSucces: boolean, userId: string, accessToken: string): void {
    if (isSucces) {
      //response.authResponse.accessToken
      this.reportSocialLogin(this.authenticateSocial("facebook", userId, accessToken));
    }
  }

  public handleGoogleSignInResponse(isSucces: boolean, userId: string, accessToken: string): void {
    if (isSucces) {
      this.reportSocialLogin(this.authenticateSocial("google", userId, accessToken));
    }
  }

  private handleAppleSignInResponse(isSuccess: boolean, userId: string, accessToken: string): void {
    if (isSuccess) {
      this.reportSocialLogin(this.authenticateSocial("apple", userId, accessToken, true));
    }
  }

  /** Forwards a social sign-in outcome to the account dialog; a rejection or HTTP error always carries a message. */
  private reportSocialLogin(login$: Observable<any>): void {
    const fallback = this.languageService.languageJSON.AccountDialog_SignIn_Error;
    login$.subscribe({
      next: (resp) => this.ObservableSocialLoginResponse.next({
        AuthResponse: resp.success,
        ErrorMessage: resp.success ? null : (resp.failed || fallback)
      }),
      error: () => this.ObservableSocialLoginResponse.next({ AuthResponse: null, ErrorMessage: fallback })
    });
  }

  /* sign in wih facebook */
  public signInWithFacebook(): void {
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.AddFacebookSignInCallback((isSucces: boolean, userId: string, accessToken: string) => { this.handleFacebookSignInResponse(isSucces, userId, accessToken); });
      this.webviewService.FacebookSignIn();
    }
    else {
      // The SDK loads on the first click. FB.login opens a popup, which browsers allow only for a
      // short while after the click, so a very slow load can still get that first popup blocked.
      const login = () => window['FB'].login((response) => {
        if (response.authResponse) {
          this.handleFacebookSignInResponse(response.authResponse, response.authResponse.userID, response.authResponse.accessToken);
        }
      }, { scope: 'email' });
      if (window['FB']) {
        login();
      } else {
        this.scriptLoader.load(FACEBOOK_SDK_SRC).then(login, () => this.reportSocialSdkFailure());
      }
    }
  }

  /** A sign-in SDK did not load (offline, blocked by an extension): shown like a failed sign-in. */
  private reportSocialSdkFailure(): void {
    this.ObservableSocialLoginResponse.next({ AuthResponse: null, ErrorMessage: this.languageService.languageJSON.AccountDialog_SignIn_Error });
  }

  private signOutFacebook(): void {
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.FacebookLogout();
    }
    // No SDK means Facebook sign-in was not used in this page load, so there is no session to end.
    else if (window['FB']) {
      window['FB'].getLoginStatus(function (response) {
        if (response?.status === 'connected') {
          window['FB'].logout(function (response) {
          });
        }
      });
    }
  }

  /* apple login */
  public signInWithApple(): void {
    if (this.webviewService.IsGPSTWebview() && device.ios()) {
      this.webviewService.AppleSignIn();
    }
    else {
      const signIn = () => {
        window["AppleID"].auth.init({
          clientId: environment.APPLE_CLIENT_ID,
          redirectURI: environment.APPLE_REDIRECT_URI,
          scope: "name email"
        });
        window["AppleID"].auth.signIn();
      };
      // Normally loaded by prepareSocialSignIn() already; Apple redirects, so waiting for it is fine.
      this.scriptLoader.load(APPLE_SIGN_IN_SRC).then(signIn, () => this.reportSocialSdkFailure());
    }
  }

  public signInWithGoogle(): void {
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.AddGoogleSignInCallback((isSucces: boolean, userId: string, accessToken: string) => { this.handleGoogleSignInResponse(isSucces, userId, accessToken); });
      this.webviewService.GoogleSignIn();
    }
    else {
      this.clickGisButton();
    }
  }

  /**
   * Clicks the hidden GIS button rendered in initGoogleLogin(); the credential arrives
   * in the callback registered there. Called from a user click, so the popup keeps the
   * user activation while we briefly wait for Google's markup if it is still loading.
   */
  private clickGisButton(attempt: number = 0): void {
    const gisButton = this.gisButtonHost?.querySelector<HTMLElement>('[role="button"]');
    if (gisButton) {
      gisButton.click();
      return;
    }

    if (attempt < AccountService.GIS_POLL_MAX) {
      setTimeout(() => this.clickGisButton(attempt + 1), AccountService.GIS_POLL_MS);
      return;
    }

    console.warn('Google Sign-In: the GIS button was not rendered (gsi/client script missing or blocked?)');
  }

  private signOutGoogle(): void {
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.GoogleLogOut();
    }
  }
}
